import {
	type DroppablePayloadBase,
	getDraggableType,
	getDroppablePayload,
	type TreeItem,
} from "@msuite/ui/dnd";
import { guardNull } from "@msuite/utils/misc";
import type { CalculationFormServiceItem } from "@msuite/utils/schemas";
import { generateId } from "@msuite/utils/strings";
import type { Nullable, Tables } from "@msuite/utils/types";

export class CalculationFormHandler {
	items: Nullable<CalculationFormServiceItem[]> = [];

	constructor(items: Nullable<CalculationFormServiceItem[]>) {
		this.items = items || [];
	}

	/**
	 * This function creates a tree of items.
	 * @returns
	 */
	createTree(): Record<string, TreeItem<CalculationFormServiceItem>> {
		const tree: Record<string, TreeItem<CalculationFormServiceItem>> = {};
		const rootElements = this.getRootElements();
		tree.root = {
			id: "root",
			data: undefined,
			children: rootElements.map((item) => item.id),
		} as TreeItem<CalculationFormServiceItem>;

		for (const item of this.items || []) {
			if (!item.id) continue;
			tree[item.id] = {
				id: item.id,
				data: item,
				children: (this.items
					?.filter((child) => child.parentServiceCalculationItemId === item.id)
					.map((i) => i.id) || []) as string[],
			};
		}

		return tree;
	}

	/**
	 * This function transforms a tree data record back into a flat array of items by
	 * walking the record's children references from the root. The record is the mutation
	 * target of every tree operation and therefore always reflects the latest structure —
	 * unlike the tree instance's flattened item list, which is rebuilt asynchronously and
	 * can be stale when re-renders interleave with a drop.
	 * @param record
	 * @returns
	 */
	transformRecordToForm(record: Record<string, TreeItem<CalculationFormServiceItem>>) {
		const flatArray: CalculationFormServiceItem[] = [];
		const visited = new Set<string>();
		const walk = (id: string, parentId: string | null, ranking: number) => {
			if (visited.has(id)) return;
			visited.add(id);
			const node = record[id];
			if (!node) return;
			const data = node.data;
			if (data) {
				const prev = this.items?.find((i) => i.id === id);
				if (prev) Object.assign(data, prev);
				data.parentServiceCalculationItemId = guardNull(parentId);
				data.id = id;
				data.ranking = ranking;
				flatArray.push(data);
			}
			const childParentId = id === "root" ? null : id;
			let childRanking = 1;
			for (const childId of node.children || []) {
				walk(childId, childParentId, childRanking);
				childRanking++;
			}
		};
		walk("root", null, 0);
		return flatArray;
	}

	private getRootElements() {
		if (!this.items) return [];
		const rootElements = this.items.filter((item) => !item.parentServiceCalculationItemId);
		return rootElements;
	}

	handleOnProcessDrop(payload: DroppablePayloadBase) {
		const type = getDraggableType(payload);
		if (type !== "service") return null;
		const { item } = getDroppablePayload<Tables<"organizationServicesWithFullNumber">>(payload);
		if (!item) return null;
		this.items?.push({
			...item,
			type: "service",
			ranking: 0,
			id: generateId(),
			serviceId: item.id || "",
		} as unknown as CalculationFormServiceItem);
		return this.items;
	}

	getItemIndex(itemId: string) {
		return this.items?.findIndex((item) => item.id === itemId) ?? -1;
	}

	/**
	 * Duplicates a block item together with all of its descendants. Every clone
	 * receives a fresh id, cloned children are re-linked to their cloned parent,
	 * and position ids are cleared so the server inserts new position rows
	 * instead of re-pointing the originals.
	 * @param blockId
	 * @returns A new flat items array containing the clones right after the
	 * original block's subtree, or null if the block was not found.
	 */
	duplicateBlock(blockId: string) {
		const items = this.items || [];
		const blockIndex = this.getItemIndex(blockId);
		const block = items[blockIndex];
		if (!block) return null;

		const clones: CalculationFormServiceItem[] = [];
		const cloneSubtree = (source: CalculationFormServiceItem, parentId: string | null) => {
			const clone = JSON.parse(JSON.stringify(source)) as CalculationFormServiceItem;
			clone.id = generateId();
			clone.parentServiceCalculationItemId = parentId;
			for (const position of clone.positions || []) {
				position.id = null;
			}
			clones.push(clone);
			for (const child of items) {
				if (child.parentServiceCalculationItemId === source.id) {
					cloneSubtree(child, clone.id);
				}
			}
		};
		cloneSubtree(block, guardNull(block.parentServiceCalculationItemId));
		const next = [...items];
		next.splice(this.getSubtreeEndIndex(blockIndex), 0, ...clones);
		return next;
	}

	/**
	 * Returns the index right after the last descendant of the item at the given
	 * index, assuming the flat array is in depth-first order (as produced by
	 * transformRecordToForm).
	 * @param startIndex
	 * @returns
	 */
	private getSubtreeEndIndex(startIndex: number) {
		const items = this.items || [];
		const subtreeIds = new Set<string>();
		const startId = items[startIndex]?.id;
		if (startId) subtreeIds.add(startId);
		let endIndex = startIndex + 1;
		for (let i = startIndex + 1; i < items.length; i++) {
			const item = items[i];
			const parentId = item?.parentServiceCalculationItemId;
			if (!parentId || !subtreeIds.has(parentId)) continue;
			if (item?.id) subtreeIds.add(item.id);
			endIndex = i + 1;
		}
		return endIndex;
	}
}
